/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { isDevMode } from "@/lib/env"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-btn-primary text-primary-foreground",
        destructive:
          "bg-destructive text-foreground hover:bg-destructive-hover",
        outline:
          "text-primary border border-primary bg-background hover:text-secondary hover:border-secondary disabled:hover:text-primary disabled:hover:border-primary",
        secondary:
          "bg-btn-secondary font-semibold text-secondary-foreground hover:text-popover",
        ghost: "text-secondary hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 underline hover:text-secondary",
        icon: "text-primary"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    // `aria-label` / `aria-labelledby` are typed on props via
    // React.ButtonHTMLAttributes — no cast needed.
    const ariaLabel = props['aria-label']
    const ariaLabelledBy = props['aria-labelledby']

    // Dev-only accessibility warning for icon-only buttons missing a label.
    // Runs in an effect (not in the render body) so it fires once on mount
    // and on dependency change — not on every re-render. React StrictMode
    // double-invokes render in dev but effects stay once-per-mount.
    //
    // Guarded with `!asChild` because when asChild=true, Button renders a
    // Slot around a consumer-supplied element whose accessibility tree is
    // owned by the consumer's child — our prop absence is not authoritative.
    React.useEffect(() => {
      if (
        isDevMode() &&
        !asChild &&
        size === 'icon' &&
        !ariaLabel &&
        !ariaLabelledBy
      ) {
        console.warn(
          '[Button] Icon-only Button (size="icon") requires an aria-label or aria-labelledby prop. ' +
          'This button will be unreadable to screen readers. ' +
          'See brand-guide §15 Accessibility Standards.'
        )
      }
    }, [asChild, size, ariaLabel, ariaLabelledBy])

    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
